import React, { useEffect, useMemo, useState, useRef } from 'react';
import { CalendarIcon, UsersIcon, UserIcon, BriefcaseIcon, TrendingUpIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon, ClockIcon, BarChart3Icon, ChevronRightIcon as ArrowRightIcon, ListIcon, DownloadIcon, FileSpreadsheetIcon } from 'lucide-react';
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Badge, getStatusBadgeVariant } from '../ui/Badge';
import { Button } from '../ui/Button';
import { SortableHeader } from '../ui/SortableHeader';
import { useData } from '../../contexts/DataContext';
import { formatCurrency, formatTime, getToday, getTomorrow, getDaysInMonth, getFirstDayOfMonth, getMonthName, getStatusName, getDoctorPrefix } from '../../utils/helpers';
import { hasPermission } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardRevenueByUser, DashboardRevenueByDoctor } from './DashboardRevenue';
import { DashboardAppointmentsByUser } from './DashboardAppointmentsByUser';
import { DashboardProductRevenue } from './DashboardProducts';
import { exportServiceDetailToPdf, exportDashboardChartDataToPdf } from '../../utils/pdfExport';
import { exportDashboardChartDataToExcel } from '../../utils/excelExport';
import { useSortableData } from '../../hooks/useSortableData';
interface DashboardProps {
  onNavigate: (module: string) => void;
}
export function Dashboard({
  onNavigate
}: DashboardProps) {
  const {
    patients,
    doctors,
    appointments,
    services,
    opportunities,
    contacts,
    getPatient,
    getDoctor,
    getService,
    getSpecialty,
    getAppointmentsByDate,
    getTodayAppointmentsCount,
    getTotalOpportunitiesAmount
  } = useData();
  const {
    user
  } = useAuth();
  const userRole = user?.role || '';
  const normalize = (name: string) => name ? name.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '') : '';
  const cleanRole = normalize(userRole);
  const canViewStats = hasPermission(userRole, 'dashboard_stats', 'read');
  const canViewCalendar = hasPermission(userRole, 'dashboard_calendar', 'read');
  const canViewPipeline = hasPermission(userRole, 'dashboard_pipeline', 'read');
  const canViewProducts = hasPermission(userRole, 'products', 'read');
  // Only admin, supervisor and recepcion can download PDFs/Excel of charts/details
  const canDownloadPdf = cleanRole === 'administrador' || cleanRole === 'supervisor' || cleanRole === 'recepcion' || cleanRole === 'recepcionista';
  // Detect if user is a doctor/terapeuta and find their doctor record
  const isDoctorUser = useMemo(() => {
    const role = userRole.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return role === 'doctor' || role === 'terapeuta';
  }, [userRole]);
  const userDoctorId = useMemo(() => {
    if (!isDoctorUser || !user) return null;
    const matchedDoctor = doctors.find(d => d.email?.toLowerCase().trim() === user.email?.toLowerCase().trim());
    return matchedDoctor?.id || null;
  }, [isDoctorUser, user, doctors]);
  const getManualPermission = (moduleKey: string) => {
    try {
      const saved = localStorage.getItem('savidna-role-permissions');
      if (!saved) return false;
      const perms = JSON.parse(saved);
      const level = perms[moduleKey]?.[cleanRole];
      return level === 'read' || level === 'full';
    } catch (e) {
      return false;
    }
  };
  const canViewRevenueByUser = cleanRole === 'administrador' || cleanRole === 'admin' || hasPermission(cleanRole, 'dashboard_revenue_user', 'read') || getManualPermission('dashboard_revenue_user');
  const canViewRevenueByDoctor = cleanRole === 'administrador' || cleanRole === 'admin' || hasPermission(cleanRole, 'dashboard_revenue_doctor', 'read') || getManualPermission('dashboard_revenue_doctor');
  const canViewAppointmentsByUser = cleanRole === 'administrador' || cleanRole === 'admin' || hasPermission(cleanRole, 'dashboard_appointments_by_user', 'read') || getManualPermission('dashboard_appointments_by_user');
  const canViewRecentAppointments = cleanRole === 'administrador' || cleanRole === 'admin' || hasPermission(cleanRole, 'dashboard_recent_appointments', 'read') || getManualPermission('dashboard_recent_appointments');
  const canViewServiceDetail = cleanRole === 'administrador' || cleanRole === 'admin' || hasPermission(cleanRole, 'dashboard_service_detail', 'read') || getManualPermission('dashboard_service_detail');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [doctorDateFrom, setDoctorDateFrom] = useState<string>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d.toISOString().split('T')[0];
  });
  const [doctorDateTo, setDoctorDateTo] = useState<string>(getToday());
  const today = getToday();
  const selectedDateAppointments = useMemo(() => {
    const allApts = getAppointmentsByDate(selectedDate);
    if (isDoctorUser && userDoctorId) {
      return allApts.filter(a => a.doctorId === userDoctorId);
    }
    return allApts;
  }, [selectedDate, isDoctorUser, userDoctorId, getAppointmentsByDate]);
  // KPIs
  const totalAppointments = appointments.length;
  const todayCount = getTodayAppointmentsCount();
  const activeDoctors = doctors.length;
  const totalServices = services.length;
  const totalContacts = contacts.length;
  const totalOpportunities = opportunities.length;
  // Appointments Statistics (last 7 days trend only)
  const appointmentStats = useMemo(() => {
    const last7Days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      // Use timezone-aware date string instead of toISOString() which uses UTC
      const dateStr = date.toLocaleDateString('en-CA', {
        timeZone: 'America/Santo_Domingo'
      });
      const dayAppointments = appointments.filter(a => a.date === dateStr);
      last7Days.push({
        date: date.toLocaleDateString('es-ES', {
          weekday: 'short',
          day: 'numeric',
          timeZone: 'America/Santo_Domingo'
        }),
        total: dayAppointments.length,
        completadas: dayAppointments.filter(a => a.status === 'completada').length,
        programadas: dayAppointments.filter(a => a.status === 'programada').length,
        canceladas: dayAppointments.filter(a => a.status === 'cancelada').length
      });
    }
    return {
      last7Days
    };
  }, [appointments]);
  // Top Doctores por Citas - filtered by date range
  const doctorChartData = useMemo(() => {
    const filteredAppointments = appointments.filter(apt => {
      if (doctorDateFrom && apt.date < doctorDateFrom) return false;
      if (doctorDateTo && apt.date > doctorDateTo) return false;
      return true;
    });
    const byDoctor = filteredAppointments.reduce((acc, apt) => {
      const doctor = getDoctor(apt.doctorId);
      const doctorName = doctor?.name || 'Sin asignar';
      acc[doctorName] = (acc[doctorName] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    return Object.entries(byDoctor).map(([name, count]) => ({
      name,
      citas: count
    })).sort((a, b) => b.citas - a.citas).slice(0, 5);
  }, [appointments, getDoctor, doctorDateFrom, doctorDateTo]);
  // Service Detail Statistics
  const serviceDetailStats = useMemo(() => {
    const completedAppointments = appointments.filter(a => a.status === 'completada');
    const serviceMap: Record<string, {
      name: string;
      count: number;
      totalRevenue: number;
    }> = {};
    completedAppointments.forEach(apt => {
      const serviceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId].filter(Boolean);
      serviceIds.forEach(sId => {
        const service = getService(sId);
        if (service) {
          if (!serviceMap[service.id]) serviceMap[service.id] = {
            name: service.name,
            count: 0,
            totalRevenue: 0
          };
          serviceMap[service.id].count++;
          serviceMap[service.id].totalRevenue += service.price;
        }
      });
    });
    const grandTotalRevenue = Object.values(serviceMap).reduce((sum, s) => sum + s.totalRevenue, 0);
    const grandTotalCount = Object.values(serviceMap).reduce((sum, s) => sum + s.count, 0);
    const details = Object.values(serviceMap).map(s => ({
      ...s,
      avgPerAppointment: s.count > 0 ? s.totalRevenue / s.count : 0,
      percentOfTotal: grandTotalRevenue > 0 ? s.totalRevenue / grandTotalRevenue * 100 : 0
    })).sort((a, b) => b.totalRevenue - a.totalRevenue);
    return {
      details,
      grandTotalRevenue,
      grandTotalCount
    };
  }, [appointments, getService]);
  // Pipeline stages
  const pipelineStages = [{
    id: 'nuevo_lead',
    name: 'Nuevo Lead',
    color: 'from-blue-500 to-blue-600',
    bgLight: 'bg-blue-50 dark:bg-blue-900/20',
    textColor: 'text-blue-600 dark:text-blue-400'
  }, {
    id: 'contactado',
    name: 'Contactado',
    color: 'from-cyan-500 to-cyan-600',
    bgLight: 'bg-cyan-50 dark:bg-cyan-900/20',
    textColor: 'text-cyan-600 dark:text-cyan-400'
  }, {
    id: 'evaluacion_consulta',
    name: 'Evaluación / Consulta',
    color: 'from-purple-500 to-purple-600',
    bgLight: 'bg-purple-50 dark:bg-purple-900/20',
    textColor: 'text-purple-600 dark:text-purple-400'
  }, {
    id: 'cotizacion_enviada',
    name: 'Cotización Enviada',
    color: 'from-amber-500 to-amber-600',
    bgLight: 'bg-amber-50 dark:bg-amber-900/20',
    textColor: 'text-amber-600 dark:text-amber-400'
  }, {
    id: 'confirmado',
    name: 'Confirmado',
    color: 'from-emerald-500 to-emerald-600',
    bgLight: 'bg-emerald-50 dark:bg-emerald-900/20',
    textColor: 'text-emerald-600 dark:text-emerald-400'
  }, {
    id: 'cita_agendada',
    name: 'Cita Agendada',
    color: 'from-teal-500 to-teal-600',
    bgLight: 'bg-teal-50 dark:bg-teal-900/20',
    textColor: 'text-teal-600 dark:text-teal-400'
  }, {
    id: 'servicio_realizado',
    name: 'Servicio Realizado',
    color: 'from-green-500 to-green-600',
    bgLight: 'bg-green-50 dark:bg-green-900/20',
    textColor: 'text-green-600 dark:text-green-400'
  }];
  const getStageCount = (stage: string) => opportunities.filter(o => o.stage === stage).length;
  const getStageAmount = (stage: string) => opportunities.filter(o => o.stage === stage).reduce((sum, o) => sum + o.amount, 0);
  // Calendar
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const calendarDays: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let i = 1; i <= daysInMonth; i++) calendarDays.push(i);
  const getDateString = (day: number) => `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const getAppointmentCountForDay = (day: number) => {
    const dateStr = getDateString(day);
    return appointments.filter(a => {
      if (a.date !== dateStr || a.status === 'cancelada') return false;
      if (isDoctorUser && userDoctorId && a.doctorId !== userDoctorId) return false;
      return true;
    }).length;
  };
  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const recentAppointments = [...appointments].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 5);
  // Mapped recent appointments with resolved names for sorting
  const recentAppointmentsMapped = recentAppointments.map(apt => {
    const patient = getPatient(apt.patientId);
    const doctor = getDoctor(apt.doctorId);
    const service = getService(apt.serviceId);
    return {
      ...apt,
      patientName: patient?.name || '-',
      doctorName: doctor ? `${getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)} ${doctor.name}` : '-',
      serviceName: service?.name || '-'
    };
  });
  const {
    sortedItems: sortedRecentAppointments,
    requestSort: recentRequestSort,
    isSorted: recentIsSorted
  } = useSortableData(recentAppointmentsMapped);
  // Sortable service detail stats
  const {
    sortedItems: sortedServiceDetails,
    requestSort: serviceRequestSort,
    isSorted: serviceIsSorted
  } = useSortableData(serviceDetailStats.details);
  const formattedDate = new Date().toLocaleDateString('es-ES', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const getStatusAccentColor = (status: string) => {
    switch (status) {
      case 'programada':
        return 'bg-teal-500';
      case 'completada':
        return 'bg-emerald-500';
      case 'cancelada':
        return 'bg-rose-500';
      case 'reprogramada':
        return 'bg-amber-500';
      default:
        return 'bg-gray-400';
    }
  };
  return <div className="space-y-6" data-id="element-1815">
      {/* Welcome Hero Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 p-6 md:p-8 text-white shadow-xl" data-id="element-1816">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-1817" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-cyan-400/20 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4" data-id="element-1818" />
        <div className="absolute top-1/2 right-1/4 w-32 h-32 bg-teal-300/10 rounded-full blur-xl" data-id="element-1819" />
        <div className="relative z-10" data-id="element-1820">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6" data-id="element-1821">
            <div data-id="element-1822">
              <div className="flex items-center gap-2 mb-2" data-id="element-1823">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-teal-200" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-1824">
                  <path d="M12 21c-4-4-8-6.5-8-10.5a4.5 4.5 0 0 1 9 0 4.5 4.5 0 0 1 9 0c0 4-4 6.5-8 10.5" data-id="element-1825" />
                  <path d="M12 6c0-2.5 2-4 4-4" data-id="element-1826" />
                  <path d="M12 6c0-2.5-2-4-4-4" data-id="element-1827" />
                </svg>
                <span className="text-teal-100 text-sm font-medium capitalize" data-id="element-1828">
                  {formattedDate}
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2" data-id="element-1829">
                {user?.gender === 'femenino' ? 'Bienvenida' : 'Bienvenido'},{' '}
                {user?.name?.split(' ')[0] || 'Usuario'}
              </h1>
              <p className="text-teal-100 text-lg" data-id="element-1830">
                Panel de control de Savidna
              </p>
              <div className="flex flex-wrap gap-3 mt-4" data-id="element-1831">
                <button onClick={() => onNavigate('appointments:today')} className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-sm rounded-full border border-white/20 hover:bg-white/25 transition-colors cursor-pointer" data-id="element-1832">
                  <ClockIcon className="w-4 h-4" data-id="element-1833" />
                  <span className="font-semibold" data-id="element-1834">{todayCount}</span>
                  <span className="text-teal-100 text-sm" data-id="element-1835">citas hoy</span>
                </button>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-sm rounded-full border border-white/20" data-id="element-1836">
                  <UsersIcon className="w-4 h-4" data-id="element-1837" />
                  <span className="font-semibold" data-id="element-1838">{patients.length}</span>
                  <span className="text-teal-100 text-sm" data-id="element-1839">pacientes</span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-1840">
              {hasPermission(userRole, 'patients', 'full') && <Button variant="outline" leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1842" />} onClick={() => onNavigate('patients')} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-1841">
                  Nuevo Paciente
                </Button>}
              {hasPermission(userRole, 'appointments', 'full') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1844" />} onClick={() => onNavigate('appointments')} className="bg-white text-teal-700 hover:bg-teal-50 shadow-lg shadow-teal-900/20" data-id="element-1843">
                  Nueva Cita
                </Button>}
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      {canViewStats && <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-1845">
          {[{
        icon: CalendarIcon,
        value: totalAppointments,
        label: 'Citas Totales',
        gradient: 'from-teal-500 to-cyan-500',
        module: 'appointments'
      }, {
        icon: ClockIcon,
        value: todayCount,
        label: 'Citas Hoy',
        gradient: 'from-emerald-500 to-green-500',
        module: 'appointments:today'
      }, {
        icon: UserIcon,
        value: activeDoctors,
        label: 'Doctores',
        gradient: 'from-purple-500 to-indigo-500',
        module: 'doctors'
      }, {
        icon: BriefcaseIcon,
        value: totalServices,
        label: 'Servicios',
        gradient: 'from-orange-500 to-amber-500',
        module: 'services'
      }, {
        icon: UsersIcon,
        value: totalContacts,
        label: 'Contactos',
        gradient: 'from-yellow-500 to-amber-500',
        module: 'contacts'
      }, {
        icon: TrendingUpIcon,
        value: totalOpportunities,
        label: 'Oportunidades',
        gradient: 'from-rose-500 to-pink-500',
        module: 'opportunities'
      }].map((kpi, index) => {
        const Icon = kpi.icon;
        return <Card key={index} className="cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 overflow-hidden group" onClick={() => onNavigate(kpi.module)} data-id="element-1846">
                <div className={`absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b ${kpi.gradient}`} data-id="element-1847" />
                <div className="flex items-center gap-3 pl-3" data-id="element-1848">
                  <div className={`p-2.5 rounded-xl bg-gradient-to-br ${kpi.gradient} shadow-lg`} data-id="element-1849">
                    <Icon className="w-5 h-5 text-white" data-id="element-1850" />
                  </div>
                  <div data-id="element-1851">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1852">
                      {kpi.value}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-medium" data-id="element-1853">
                      {kpi.label}
                    </p>
                  </div>
                </div>
              </Card>;
      })}
        </div>}

      {/* Stats Charts */}
      {canViewStats && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1854">
          <Card padding="none" className="overflow-hidden" data-id="element-1855">
            <div className="h-1 bg-gradient-to-r from-teal-500 to-cyan-500" data-id="element-1856" />
            <div className="p-4 md:p-6" data-id="element-1857">
              <CardHeader className="px-0 pt-0" data-id="element-1858">
                <div className="flex items-center justify-between" data-id="element-1859">
                  <CardTitle className="flex items-center gap-2" data-id="element-1860">
                    <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30" data-id="element-1861">
                      <TrendingUpIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" data-id="element-1862" />
                    </div>
                    Tendencia Últimos 7 Días
                  </CardTitle>
                  {canDownloadPdf && <button onClick={() => {
                const cols = [{
                  header: 'Fecha',
                  key: 'fecha'
                }, {
                  header: 'Total',
                  key: 'total'
                }, {
                  header: 'Completadas',
                  key: 'completadas'
                }, {
                  header: 'Programadas',
                  key: 'programadas'
                }, {
                  header: 'Canceladas',
                  key: 'canceladas'
                }];
                const chartRows = appointmentStats.last7Days.map(d => ({
                  label: d.date,
                  values: {
                    fecha: d.date,
                    total: d.total,
                    completadas: d.completadas,
                    programadas: d.programadas,
                    canceladas: d.canceladas
                  }
                }));
                exportDashboardChartDataToPdf(chartRows, cols, 'Tendencia Citas Últimos 7 Días');
              }} className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-1863">
                      <DownloadIcon className="w-4 h-4" data-id="element-1864" />
                    </button>}
                </div>
              </CardHeader>
              <CardContent className="px-0 pb-0" data-id="element-1865">
                <ResponsiveContainer width="100%" height={280} data-id="element-1866">
                  <AreaChart data={appointmentStats.last7Days} data-id="element-1867">
                    <defs data-id="element-1868">
                      <linearGradient id="colorCompletadas" x1="0" y1="0" x2="0" y2="1" data-id="element-1869">
                        <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.3} data-id="element-1870" />
                        <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} data-id="element-1871" />
                      </linearGradient>
                      <linearGradient id="colorProgramadas" x1="0" y1="0" x2="0" y2="1" data-id="element-1872">
                        <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3} data-id="element-1873" />
                        <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} data-id="element-1874" />
                      </linearGradient>
                      <linearGradient id="colorCanceladas" x1="0" y1="0" x2="0" y2="1" data-id="element-1875">
                        <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.3} data-id="element-1876" />
                        <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} data-id="element-1877" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} data-id="element-1878" />
                    <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} data-id="element-1879" />
                    <YAxis stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} data-id="element-1880" />
                    <Tooltip contentStyle={{
                  backgroundColor: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                  padding: '12px 16px'
                }} data-id="element-1881" />
                    <Legend wrapperStyle={{
                  paddingTop: '20px'
                }} data-id="element-1882" />
                    <Area type="monotone" dataKey="completadas" stroke="#14b8a6" strokeWidth={2} fill="url(#colorCompletadas)" name="Completadas" data-id="element-1883" />
                    <Area type="monotone" dataKey="programadas" stroke="#0ea5e9" strokeWidth={2} fill="url(#colorProgramadas)" name="Programadas" data-id="element-1884" />
                    <Area type="monotone" dataKey="canceladas" stroke="#f43f5e" strokeWidth={2} fill="url(#colorCanceladas)" name="Canceladas" data-id="element-1885" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </div>
          </Card>

          <Card padding="none" className="overflow-hidden" data-id="element-1886">
            <div className="h-1 bg-gradient-to-r from-purple-500 to-indigo-500" data-id="element-1887" />
            <div className="p-4 md:p-6" data-id="element-1888">
              <CardHeader className="px-0 pt-0" data-id="element-1889">
                <div className="flex items-center justify-between flex-wrap gap-2" data-id="element-1890">
                  <CardTitle className="flex items-center gap-2" data-id="element-1891">
                    <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30" data-id="element-1892">
                      <UserIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" data-id="element-1893" />
                    </div>
                    Top Doctores y Terapeutas por Citas
                  </CardTitle>
                  <div className="flex items-center gap-2" data-id="element-1894">
                    {canDownloadPdf && doctorChartData.length > 0 && <button onClick={() => {
                  const cols = [{
                    header: 'Doctor',
                    key: 'nombre'
                  }, {
                    header: 'Citas',
                    key: 'citas'
                  }];
                  const chartRows = doctorChartData.map(d => ({
                    label: d.name,
                    values: {
                      nombre: d.name,
                      citas: d.citas
                    }
                  }));
                  exportDashboardChartDataToPdf(chartRows, cols, 'Top Doctores y Terapeutas por Citas', {
                    from: doctorDateFrom,
                    to: doctorDateTo
                  });
                }} className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-1895">
                        <DownloadIcon className="w-4 h-4" data-id="element-1896" />
                      </button>}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3" data-id="element-1897">
                  <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800 p-1 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-1898">
                    <input type="date" className="bg-transparent text-xs border-none focus:ring-0 p-1 text-gray-700 dark:text-gray-300" value={doctorDateFrom} onChange={e => setDoctorDateFrom(e.target.value)} data-id="element-1899" />
                    <span className="text-gray-300 text-xs" data-id="element-1900">→</span>
                    <input type="date" className="bg-transparent text-xs border-none focus:ring-0 p-1 text-gray-700 dark:text-gray-300" value={doctorDateTo} onChange={e => setDoctorDateTo(e.target.value)} data-id="element-1901" />
                  </div>
                  <span className="text-xs text-gray-400 dark:text-gray-500" data-id="element-1902">
                    {doctorChartData.reduce((sum, d) => sum + d.citas, 0)} citas
                  </span>
                </div>
              </CardHeader>
              <CardContent className="px-0 pb-0" data-id="element-1903">
                {doctorChartData.length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700" data-id="element-1904">
                    <UserIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" data-id="element-1905" />
                    <p className="text-gray-500 dark:text-gray-400 font-medium" data-id="element-1906">
                      No hay datos en este rango
                    </p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mt-1" data-id="element-1907">
                      Ajusta las fechas para ver resultados
                    </p>
                  </div> : <ResponsiveContainer width="100%" height={280} data-id="element-1908">
                    <BarChart data={doctorChartData} layout="vertical" data-id="element-1909">
                      <defs data-id="element-1910">
                        <linearGradient id="barGradient" x1="0" y1="0" x2="1" y2="0" data-id="element-1911">
                          <stop offset="0%" stopColor="#8b5cf6" data-id="element-1912" />
                          <stop offset="100%" stopColor="#6366f1" data-id="element-1913" />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={true} vertical={false} data-id="element-1914" />
                      <XAxis type="number" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} data-id="element-1915" />
                      <YAxis dataKey="name" type="category" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} width={100} data-id="element-1916" />
                      <Tooltip contentStyle={{
                  backgroundColor: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                  padding: '12px 16px'
                }} data-id="element-1917" />
                      <Bar dataKey="citas" fill="url(#barGradient)" radius={[0, 8, 8, 0]} name="Citas" data-id="element-1918" />
                    </BarChart>
                  </ResponsiveContainer>}
              </CardContent>
            </div>
          </Card>
        </div>}

      {/* Revenue Section */}
      {(canViewRevenueByUser || canViewRevenueByDoctor) && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1919">
          {canViewRevenueByUser && <DashboardRevenueByUser appointments={appointments} getService={getService} data-id="element-1920" />}
          {canViewRevenueByDoctor && <DashboardRevenueByDoctor appointments={appointments} doctors={doctors} getService={getService} getDoctor={getDoctor} data-id="element-1921" />}
        </div>}

      {/* Products Revenue Widget */}
      {canViewProducts && <DashboardProductRevenue data-id="element-1922" />}

      {/* Pipeline */}
      {canViewPipeline && <Card padding="none" className="overflow-hidden" data-id="element-1923">
          <div className="h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-emerald-500" data-id="element-1924" />
          <div className="p-4 md:p-6" data-id="element-1925">
            <CardHeader className="px-0 pt-0" data-id="element-1926">
              <CardTitle className="flex items-center gap-2" data-id="element-1927">
                <div className="p-2 rounded-lg bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30" data-id="element-1928">
                  <TrendingUpIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-1929" />
                </div>
                Pipeline de Ingresos
              </CardTitle>
            </CardHeader>
            <CardContent className="px-0 pb-0" data-id="element-1930">
              <div className="flex gap-2 overflow-x-auto pb-2" data-id="element-1931">
                {pipelineStages.map((stage, index) => {
              const count = getStageCount(stage.id);
              const amount = getStageAmount(stage.id);
              return <div key={stage.id} className="flex items-center" data-id="element-1932">
                      <div className={`flex-1 min-w-[140px] p-4 ${stage.bgLight} rounded-xl border border-gray-100 dark:border-gray-700/50 relative overflow-hidden`} data-id="element-1933">
                        <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${stage.color}`} data-id="element-1934" />
                        <div className="flex items-center gap-2 mb-2" data-id="element-1935">
                          <div className={`w-2.5 h-2.5 rounded-full bg-gradient-to-r ${stage.color}`} data-id="element-1936" />
                          <span className={`text-sm font-semibold ${stage.textColor}`} data-id="element-1937">
                            {stage.name}
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1938">
                          {count}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1939">
                          {formatCurrency(amount)}
                        </p>
                      </div>
                      {index < pipelineStages.length - 1 && <ArrowRightIcon className="w-5 h-5 text-gray-300 dark:text-gray-600 mx-1 flex-shrink-0" data-id="element-1940" />}
                    </div>;
            })}
              </div>
            </CardContent>
          </div>
        </Card>}

      {/* Appointments by User */}
      {canViewAppointmentsByUser && <DashboardAppointmentsByUser appointments={appointments} data-id="element-1941" />}

      {/* Calendar and Appointments */}
      {canViewCalendar && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1942">
          <Card padding="none" className="overflow-hidden" data-id="element-1943">
            <div className="h-1 bg-gradient-to-r from-teal-500 to-cyan-500" data-id="element-1944" />
            <div className="p-4 md:p-6" data-id="element-1945">
              <CardHeader className="px-0 pt-0" data-id="element-1946">
                <div className="flex items-center justify-between" data-id="element-1947">
                  <CardTitle className="flex items-center gap-2" data-id="element-1948">
                    <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30" data-id="element-1949">
                      <CalendarIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" data-id="element-1950" />
                    </div>
                    Calendario
                  </CardTitle>
                  <div className="flex items-center gap-1" data-id="element-1951">
                    <button onClick={prevMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" data-id="element-1952">
                      <ChevronLeftIcon className="w-5 h-5 text-gray-500" data-id="element-1953" />
                    </button>
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 min-w-[130px] text-center" data-id="element-1954">
                      {getMonthName(month)} {year}
                    </span>
                    <button onClick={nextMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" data-id="element-1955">
                      <ChevronRightIcon className="w-5 h-5 text-gray-500" data-id="element-1956" />
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-0 pb-0" data-id="element-1957">
                <div className="grid grid-cols-7 gap-1 mb-3" data-id="element-1958">
                  {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => <div key={day} className="text-center text-xs font-semibold text-gray-400 dark:text-gray-500 py-2" data-id="element-1959">
                        {day}
                      </div>)}
                </div>
                <div className="grid grid-cols-7 gap-1" data-id="element-1960">
                  {calendarDays.map((day, index) => {
                if (day === null) return <div key={`empty-${index}`} className="aspect-square" data-id="element-1961" />;
                const dateStr = getDateString(day);
                const appointmentCount = getAppointmentCountForDay(day);
                const isToday = dateStr === today;
                const isSelected = dateStr === selectedDate;
                return <button key={day} onClick={() => setSelectedDate(dateStr)} className={`aspect-square rounded-xl text-sm font-medium relative flex flex-col items-center justify-center gap-0.5 transition-all duration-200
                          ${isToday ? 'bg-gradient-to-br from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30' : ''}
                          ${isSelected && !isToday ? 'ring-2 ring-teal-500 ring-offset-2 dark:ring-offset-gray-800 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300' : ''}
                          ${!isToday && !isSelected ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300' : ''}`} data-id="element-1962">
                        <span data-id="element-1963">{day}</span>
                        {appointmentCount > 0 && <div className={`w-1.5 h-1.5 rounded-full ${isToday ? 'bg-white' : 'bg-teal-500'}`} data-id="element-1964" />}
                      </button>;
              })}
                </div>
              </CardContent>
            </div>
          </Card>

          <Card padding="none" className="overflow-hidden" data-id="element-1965">
            <div className="h-1 bg-gradient-to-r from-emerald-500 to-teal-500" data-id="element-1966" />
            <div className="p-4 md:p-6" data-id="element-1967">
              <CardHeader className="px-0 pt-0" data-id="element-1968">
                <CardTitle className="flex items-center gap-2" data-id="element-1969">
                  <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30" data-id="element-1970">
                    <ClockIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" data-id="element-1971" />
                  </div>
                  Citas de {selectedDate === today ? 'Hoy' : selectedDate}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-0" data-id="element-1972">
                {selectedDateAppointments.length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700" data-id="element-1973">
                    <CalendarIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" data-id="element-1974" />
                    <p className="text-gray-500 dark:text-gray-400 font-medium" data-id="element-1975">
                      No hay citas programadas
                    </p>
                  </div> : <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1" data-id="element-1976">
                    {selectedDateAppointments.map(apt => {
                const patient = getPatient(apt.patientId);
                const doctor = getDoctor(apt.doctorId);
                const service = getService(apt.serviceId);
                return <div key={apt.id} className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700/50 relative overflow-hidden" data-id="element-1977">
                          <div className={`absolute left-0 top-0 bottom-0 w-1 ${getStatusAccentColor(apt.status)}`} data-id="element-1978" />
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-teal-500/20 flex-shrink-0" data-id="element-1979">
                            {patient?.name?.charAt(0).toUpperCase() || 'P'}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-1980">
                            <div className="flex items-center gap-2 mb-1" data-id="element-1981">
                              <span className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-1982">
                                {formatTime(apt.time)}
                              </span>
                              <span className="text-sm text-gray-400" data-id="element-1983">•</span>
                              <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1984">
                                {apt.duration} min
                              </span>
                            </div>
                            <p className="font-semibold text-gray-900 dark:text-white truncate" data-id="element-1985">
                              {patient?.name || 'Paciente'}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate" data-id="element-1986">
                              {doctor ? `${getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)} ${doctor.name}` : ''}{' '}
                              • {service?.name}
                            </p>
                          </div>
                          <Badge variant={getStatusBadgeVariant(apt.status)} data-id="element-1987">
                            {getStatusName(apt.status)}
                          </Badge>
                        </div>;
              })}
                  </div>}
              </CardContent>
            </div>
          </Card>
        </div>}

      {/* Recent Appointments */}
      {canViewRecentAppointments && <Card padding="none" className="overflow-hidden" data-id="element-1988">
          <div className="h-1 bg-gradient-to-r from-gray-400 to-gray-500" data-id="element-1989" />
          <div className="p-4 md:p-6" data-id="element-1990">
            <CardHeader className="px-0 pt-0" data-id="element-1991">
              <div className="flex items-center justify-between" data-id="element-1992">
                <CardTitle className="flex items-center gap-2" data-id="element-1993">
                  <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700" data-id="element-1994">
                    <CalendarIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" data-id="element-1995" />
                  </div>
                  Citas Recientes
                </CardTitle>
                <div className="flex items-center gap-2" data-id="element-1996">
                  {canDownloadPdf && recentAppointments.length > 0 && <button onClick={() => {
                const cols = [{
                  header: 'Paciente',
                  key: 'paciente'
                }, {
                  header: 'Doctor',
                  key: 'doctor'
                }, {
                  header: 'Servicio',
                  key: 'servicio'
                }, {
                  header: 'Fecha',
                  key: 'fecha'
                }, {
                  header: 'Hora',
                  key: 'hora'
                }, {
                  header: 'Estado',
                  key: 'estado'
                }];
                const chartRows = recentAppointmentsMapped.map(apt => ({
                  label: apt.patientName,
                  values: {
                    paciente: apt.patientName,
                    doctor: apt.doctorName,
                    servicio: apt.serviceName,
                    fecha: apt.date,
                    hora: formatTime(apt.time),
                    estado: getStatusName(apt.status)
                  }
                }));
                exportDashboardChartDataToPdf(chartRows, cols, 'Citas Recientes');
              }} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors" title="Descargar PDF" data-id="element-1997">
                      <DownloadIcon className="w-4 h-4" data-id="element-1998" />
                    </button>}
                  {canDownloadPdf && recentAppointments.length > 0 && <button onClick={() => {
                const cols = [{
                  header: 'Paciente',
                  key: 'paciente'
                }, {
                  header: 'Doctor',
                  key: 'doctor'
                }, {
                  header: 'Servicio',
                  key: 'servicio'
                }, {
                  header: 'Fecha',
                  key: 'fecha'
                }, {
                  header: 'Hora',
                  key: 'hora'
                }, {
                  header: 'Estado',
                  key: 'estado'
                }];
                const chartRows = recentAppointmentsMapped.map(apt => ({
                  label: apt.patientName,
                  values: {
                    paciente: apt.patientName,
                    doctor: apt.doctorName,
                    servicio: apt.serviceName,
                    fecha: apt.date,
                    hora: formatTime(apt.time),
                    estado: getStatusName(apt.status)
                  }
                }));
                exportDashboardChartDataToExcel(chartRows, cols, 'Citas Recientes');
              }} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Descargar Excel" data-id="element-1999">
                      <FileSpreadsheetIcon className="w-4 h-4" data-id="element-2000" />
                    </button>}
                  <Button variant="ghost" size="sm" onClick={() => onNavigate('appointments')} className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300" data-id="element-2001">
                    Ver todas
                    <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-2002" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-0 pb-0" data-id="element-2003">
              <div className="overflow-x-auto" data-id="element-2004">
                <table className="w-full" data-id="element-2005">
                  <thead data-id="element-2006">
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50" data-id="element-2007">
                      <SortableHeader label="Paciente" sortKey="patientName" currentDirection={recentIsSorted('patientName')} onSort={recentRequestSort} data-id="element-2008" />
                      <SortableHeader label="Doctor" sortKey="doctorName" currentDirection={recentIsSorted('doctorName')} onSort={recentRequestSort} data-id="element-2009" />
                      <SortableHeader label="Servicio" sortKey="serviceName" currentDirection={recentIsSorted('serviceName')} onSort={recentRequestSort} data-id="element-2010" />
                      <SortableHeader label="Fecha" sortKey="date" currentDirection={recentIsSorted('date')} onSort={recentRequestSort} data-id="element-2011" />
                      <SortableHeader label="Hora" sortKey="time" currentDirection={recentIsSorted('time')} onSort={recentRequestSort} data-id="element-2012" />
                      <SortableHeader label="Estado" sortKey="status" currentDirection={recentIsSorted('status')} onSort={recentRequestSort} data-id="element-2013" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-2014">
                    {sortedRecentAppointments.map(apt => <tr key={apt.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-2015">
                        <td className="py-3 px-4" data-id="element-2016">
                          <div className="flex items-center gap-3" data-id="element-2017">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white text-sm font-semibold" data-id="element-2018">
                              {apt.patientName?.charAt(0).toUpperCase() || 'P'}
                            </div>
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-2019">
                              {apt.patientName}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400" data-id="element-2020">
                          {apt.doctorName}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400" data-id="element-2021">
                          {apt.serviceName}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400" data-id="element-2022">
                          {apt.date}
                        </td>
                        <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-2023">
                          {formatTime(apt.time)}
                        </td>
                        <td className="py-3 px-4" data-id="element-2024">
                          <div className="flex items-center gap-2" data-id="element-2025">
                            <div className={`w-2 h-2 rounded-full ${getStatusAccentColor(apt.status)}`} data-id="element-2026" />
                            <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-2027">
                              {getStatusName(apt.status)}
                            </span>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </div>
        </Card>}

      {/* Service Detail */}
      {canViewServiceDetail && <Card padding="none" className="overflow-hidden" data-id="element-2028">
          <div className="h-1 bg-gradient-to-r from-orange-500 to-amber-500" data-id="element-2029" />
          <div className="p-4 md:p-6" data-id="element-2030">
            <CardHeader className="px-0 pt-0" data-id="element-2031">
              <div className="flex items-center justify-between" data-id="element-2032">
                <CardTitle className="flex items-center gap-2" data-id="element-2033">
                  <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30" data-id="element-2034">
                    <ListIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" data-id="element-2035" />
                  </div>
                  Detalle por Servicio
                </CardTitle>
                <div className="flex items-center gap-2" data-id="element-2036">
                  {canDownloadPdf && serviceDetailStats.details.length > 0 && <button onClick={() => {
                exportServiceDetailToPdf(serviceDetailStats.details, serviceDetailStats.grandTotalRevenue, serviceDetailStats.grandTotalCount);
              }} className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-2037">
                      <DownloadIcon className="w-4 h-4" data-id="element-2038" />
                    </button>}
                  <Button variant="ghost" size="sm" onClick={() => onNavigate('services')} className="text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300" data-id="element-2039">
                    Ver servicios
                    <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-2040" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-0 pb-0" data-id="element-2041">
              {serviceDetailStats.details.length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700" data-id="element-2042">
                  <BriefcaseIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" data-id="element-2043" />
                  <p className="text-gray-500 dark:text-gray-400 font-medium" data-id="element-2044">
                    No hay citas completadas aún
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1" data-id="element-2045">
                    Los datos aparecerán cuando se completen citas
                  </p>
                </div> : <div className="overflow-x-auto" data-id="element-2046">
                  <table className="w-full" data-id="element-2047">
                    <thead data-id="element-2048">
                      <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50" data-id="element-2049">
                        <SortableHeader label="Servicio" sortKey="name" currentDirection={serviceIsSorted('name')} onSort={serviceRequestSort} data-id="element-2050" />
                        <SortableHeader label="Cantidad" sortKey="count" currentDirection={serviceIsSorted('count')} onSort={serviceRequestSort} align="center" data-id="element-2051" />
                        <SortableHeader label="Ingresos Totales" sortKey="totalRevenue" currentDirection={serviceIsSorted('totalRevenue')} onSort={serviceRequestSort} align="right" data-id="element-2052" />
                        <SortableHeader label="Promedio / Cita" sortKey="avgPerAppointment" currentDirection={serviceIsSorted('avgPerAppointment')} onSort={serviceRequestSort} align="right" data-id="element-2053" />
                        <SortableHeader label="% del Total" sortKey="percentOfTotal" currentDirection={serviceIsSorted('percentOfTotal')} onSort={serviceRequestSort} align="right" data-id="element-2054" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-2055">
                      {sortedServiceDetails.map((service, index) => <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-2056">
                          <td className="py-3 px-4" data-id="element-2057">
                            <div className="flex items-center gap-3" data-id="element-2058">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white text-sm font-semibold" data-id="element-2059">
                                {service.name.charAt(0)}
                              </div>
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-2060">
                                {service.name}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center" data-id="element-2061">
                            <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 text-sm font-semibold" data-id="element-2062">
                              {service.count}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right text-sm font-semibold text-gray-900 dark:text-gray-100" data-id="element-2063">
                            {formatCurrency(service.totalRevenue)}
                          </td>
                          <td className="py-3 px-4 text-right text-sm text-gray-600 dark:text-gray-400" data-id="element-2064">
                            {formatCurrency(service.avgPerAppointment)}
                          </td>
                          <td className="py-3 px-4 text-right" data-id="element-2065">
                            <div className="flex items-center justify-end gap-2" data-id="element-2066">
                              <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-2067">
                                <div className="h-full bg-gradient-to-r from-orange-500 to-amber-500 rounded-full transition-all duration-500" style={{
                          width: `${Math.min(service.percentOfTotal, 100)}%`
                        }} data-id="element-2068" />
                              </div>
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[3rem] text-right" data-id="element-2069">
                                {service.percentOfTotal.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                        </tr>)}
                    </tbody>
                    <tfoot data-id="element-2070">
                      <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50/80 dark:bg-gray-800/80" data-id="element-2071">
                        <td className="py-3 px-4 text-sm font-bold text-gray-900 dark:text-white" data-id="element-2072">
                          TOTAL
                        </td>
                        <td className="py-3 px-4 text-center text-sm font-bold text-gray-900 dark:text-white" data-id="element-2073">
                          {serviceDetailStats.grandTotalCount}
                        </td>
                        <td className="py-3 px-4 text-right text-sm font-bold text-gray-900 dark:text-white" data-id="element-2074">
                          {formatCurrency(serviceDetailStats.grandTotalRevenue)}
                        </td>
                        <td className="py-3 px-4 text-right text-sm font-bold text-gray-600 dark:text-gray-400" data-id="element-2075">
                          {serviceDetailStats.grandTotalCount > 0 ? formatCurrency(serviceDetailStats.grandTotalRevenue / serviceDetailStats.grandTotalCount) : formatCurrency(0)}
                        </td>
                        <td className="py-3 px-4 text-right text-sm font-bold text-gray-900 dark:text-white" data-id="element-2076">
                          100%
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>}
            </CardContent>
          </div>
        </Card>}

      {/* No sections visible */}
      {!canViewStats && !canViewCalendar && !canViewPipeline && !canViewAppointmentsByUser && !canViewRevenueByUser && !canViewRevenueByDoctor && !canViewRecentAppointments && !canViewServiceDetail && <Card className="overflow-hidden" data-id="element-2077">
            <div className="py-16 text-center" data-id="element-2078">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-teal-100 to-cyan-100 dark:from-teal-900/30 dark:to-cyan-900/30 flex items-center justify-center" data-id="element-2079">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-teal-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-2080">
                  <path d="M12 21c-4-4-8-6.5-8-10.5a4.5 4.5 0 0 1 9 0 4.5 4.5 0 0 1 9 0c0 4-4 6.5-8 10.5" data-id="element-2081" />
                  <path d="M12 6c0-2.5 2-4 4-4" data-id="element-2082" />
                  <path d="M12 6c0-2.5-2-4-4-4" data-id="element-2083" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-2084">
                {user?.gender === 'femenino' ? 'Bienvenida' : 'Bienvenido'} al
                Panel de Control
              </h2>
              <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto" data-id="element-2085">
                No tienes acceso a las secciones avanzadas del panel. Contacta
                al administrador para solicitar permisos adicionales.
              </p>
            </div>
          </Card>}
    </div>;
}